import React, { useEffect, useRef, useState } from "react";
import { Flex, Box } from "@chakra-ui/react";
import { Link, Outlet, useLocation } from "react-router-dom"; // Link from react-router-dom for navigation links
import TawkToScript from "./TawkToScript";
import axios from "axios";
import Footer from "./components/Footer/Footer";
import Navbar from "./components/Navbar/Navbar";
import { useCart } from "./CartContext";
import usePageTitle from "./components/PageTitle/PageTitle";
import ReactGA from "react-ga4";
import SEO from "./components/Seo/Seo";
import RedirectLink from "./components/RedirectedLink/RedirectedLinks";
import useCurrentTime from "./hooks/useCurrentTime"; // Not used directly in App, but might be in other components
import { getDiscountInfo } from "./utils/Discount"; // Not used directly in App

// Google Analytics Initialization
ReactGA.initialize("G-66PCR1F99J");
// Send initial pageview when the component mounts
ReactGA.send({ hitType: "pageview", page: window.location.pathname });

function App() {
  const location = useLocation(); // To get the current route path
  const { showCart } = useCart(); // Hook from CartContext, potentially for cart visibility

  // State for popups
  const [showPopup, setShowPopup] = useState(false);
  const [showContactUs, setshowContactUs] = useState(false);

  // Refs for managing timeouts
  const contactIntervalRef = useRef(null); // Not currently used in the provided code
  const contactTimeoutRef = useRef(null);

  // State to track backend health
  const [backendUp, setBackendUp] = useState(true);

  // Effect to check backend health on component mount
  useEffect(() => {
    axios
      .get("/health") // Assuming a /health endpoint exists
      .then((response) => {
        if (
          response.status === 200 &&
          (response.data === "OK" || response.data.status === "ok")
        ) {
          setBackendUp(true);
        } else {
          throw new Error("Backend health check failed");
        }
      })
      .catch(() => {
        setBackendUp(false);
      });
  }, []); // Runs only once on mount

  // Effect for the first time user visits the site and a general popup
  useEffect(() => {
    const hasPopupBeenShown = sessionStorage.getItem("popupShown");
    if (!hasPopupBeenShown) {
      const firstTimeout = setTimeout(() => {
        setShowPopup(true);
        sessionStorage.setItem("popupShown", "true");
      }, 5000); // Show popup after 5 seconds
      return () => clearTimeout(firstTimeout); // Cleanup on unmount
    }
  }, []);

  // Effect for tracking first visit time and page
  useEffect(() => {
    if (!localStorage.getItem("first_visit_time")) {
      localStorage.setItem("first_visit_time", Date.now());
      localStorage.setItem("first_page", window.location.pathname);
      localStorage.setItem(
        "visit_days",
        JSON.stringify([new Date().toISOString().split("T")[0]])
      );
    }
    localStorage.setItem("entry_time", Date.now());
  }, []); // Runs only once on mount

  // Effect to track daily visits
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    let visitDays = JSON.parse(localStorage.getItem("visit_days") || "[]");
    if (!visitDays.includes(today)) {
      visitDays.push(today);
      localStorage.setItem("visit_days", JSON.stringify(visitDays));
    }
  }, []); // Runs only once on mount

  // Effect for contact popup logic
  useEffect(() => {
    const maxCount = 2;
    let count = parseInt(sessionStorage.getItem("contactPopupCount")) || 0;
    if (count >= maxCount) {
      return; // Don't show if max count reached
    }

    const showAndStartOneTimeInterval = () => {
      setshowContactUs(true);
      count++;
      sessionStorage.setItem("contactPopupCount", count);
      if (count === 1) {
        // If this is the first time showing, schedule a second potential show after 5 mins
        contactTimeoutRef.current = setTimeout(() => {
          if (parseInt(sessionStorage.getItem("contactPopupCount")) === 1) {
            setshowContactUs(true);
            sessionStorage.setItem("contactPopupCount", 2);
          }
        }, 300000); // 5 minutes
      }
    };

    // Show the first contact popup after 50 seconds
    const firstTimeout = setTimeout(() => {
      showAndStartOneTimeInterval();
    }, 50000); // 50 seconds

    return () => {
      clearTimeout(firstTimeout);
      if (contactTimeoutRef.current) {
        clearTimeout(contactTimeoutRef.current);
      }
    };
  }, [setshowContactUs]); // Dependency on setshowContactUs

  // Effect to handle cart creation/fetching from localStorage and backend
  useEffect(() => {
    const pagesToExcludeCartFetch = [
      "/return", // Example: Exclude user-info page
    ];

    const currentPagePath = location.pathname;

    if (pagesToExcludeCartFetch.includes(currentPagePath)) {
      console.log(`Cart fetch and create skipped for page: ${currentPagePath}`);
      return; // Exit the effect if on an excluded page
    }
    // --- END OF NAVIGATION GUARD ---
    const handleCart = async () => {
      let storedCart;
      try {
        storedCart = JSON.parse(localStorage.getItem("cart"));
      } catch (e) {
        // Handle potential JSON parsing errors
        console.error("Error parsing cart from localStorage:", e);
        storedCart = null;
      }

      // Flag to determine if a new cart needs to be created
      let needsNewCart = false;
      if (storedCart && storedCart._id && storedCart.hasOwnProperty("isEmi")) {
        try {
          const response = await axios.get(`cart/read/${storedCart._id}`);
          const fetchedCart = response?.data;

          if (fetchedCart?.success && fetchedCart?.hasOwnProperty("_id")) {
            if (JSON.stringify(storedCart) !== JSON.stringify(fetchedCart)) {
              localStorage.setItem("cart", JSON.stringify(fetchedCart?.result));

              console.log("Cart updated from backend to localStorage.");
            } else {
              console.log("Local cart is already up-to-date with backend.");
            }
          }
        } catch (error) {
          // Backend call failed (e.g., 404, network error).
          if (error.response?.status === 404) {
            console.warn(
              "Cart not found on backend (404). Clearing local storage."
            );

            localStorage.removeItem("cart");
            needsNewCart = true;
          } else {
            console.error("Error verifying cart with backend:", error);
            // Decide how to handle other network/server errors.
            // For now, if verification fails, assume it needs a new cart.
            needsNewCart = true;
          }
        }
      } else {
        // No valid cart found in localStorage initially.
        console.log(
          "No valid cart found in localStorage. Will attempt to create one."
        );

        localStorage.removeItem("cart"); // Ensure no corrupted data remains
        needsNewCart = true;
      }

      // If needsNewCart flag is set, proceed to create a new cart
      if (needsNewCart) {
        await createCart();
      }
    };

    // Helper function to create a new cart
    const createCart = async () => {
      try {
        // It's good to re-check localStorage here just in case something
        // else created a cart in the interim, though unlikely in a single flow.
        const currentStoredCart = JSON.parse(localStorage.getItem("cart"));
        if (
          currentStoredCart?._id &&
          currentStoredCart.hasOwnProperty("isEmi")
        ) {
          console.log("A valid cart already exists, not creating a new one.");
          return; // Exit if a valid cart was somehow created concurrently
        }

        const response = await axios.post(`cart/create`);
        const createdCart = response?.data?.result;

        if (createdCart?._id) {
          createdCart.isEmi = true;
          localStorage.setItem("cart", JSON.stringify(createdCart));
          console.log("New cart created and stored in localStorage.");
        } else {
          console.error("Failed to get cart ID from create cart response.");
        }
      } catch (error) {
        console.error("Failed to create cart:", error);
      }
    };

    handleCart();
  }, [location.pathname]);
  // UTM parameter handling
  const storeUTMParams = () => {
    const urlParams = new URLSearchParams(window.location.search);

    const utmSource = urlParams.get("utm_source");
    const utmMedium = urlParams.get("utm_medium");
    const utmCampaign = urlParams.get("utm_campaign");
    const utmId = urlParams.get("utm_id");
    const utmTerm = urlParams.get("utm_term");
    const utmContent = urlParams.get("utm_content");

    if (utmSource) {
      localStorage.setItem("utm_source", utmSource);
      if (utmMedium) localStorage.setItem("utm_medium", utmMedium);
      if (utmCampaign) localStorage.setItem("utm_campaign", utmCampaign);
      if (utmId) localStorage.setItem("utm_id", utmId);
      if (utmTerm) localStorage.setItem("utm_term", utmTerm);
      if (utmContent) localStorage.setItem("utm_content", utmContent);
    }
    // Ensure first_page is set even if UTM params are not present
    if (!localStorage.getItem("first_page")) {
      localStorage.setItem("first_page", window.location.pathname);
    }
  };

  // Event listener for UTM parameters on DOM load
  // Consider if this is necessary if you also have the useEffect below
  document.addEventListener("DOMContentLoaded", storeUTMParams);

  // Hook to manage page titles
  usePageTitle();

  // Effect to store UTM parameters on component mount (and on location change)
  useEffect(() => {
    storeUTMParams();
  }, [location.pathname]); // Re-run when path changes

  // --- Backend Maintenance View ---
  if (!backendUp) {
    return (
      <Flex
        minH="100vh"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        fontFamily="Poppins, sans-serif"
        textAlign="center"
        bg="gray.100"
        color="gray.700"
        p={4}
      >
        <Box fontSize="4xl" fontWeight="bold">
          🚧 We're Under Maintenance
        </Box>
        <Box fontSize="lg" mt={4} maxW="600px">
          Our servers are currently undergoing some scheduled updates or
          maintenance.
          <br />
          We’ll be back shortly. Thanks for your patience!
          <br />
          <br />
          In the meantime, please reach out to us:
          <br />
          Email:{" "}
          <Link
            to="mailto:sales@m2hse.co.uk" // Use Link for consistency if not navigating
            color="blue.500"
            fontWeight="bold"
          >
            sales@m2hse.co.uk
          </Link>
          <br />
          Phone:{" "}
          <Link href="tel:+02080599944" color="blue.500" fontWeight="bold">
            {" "}
            {/* tel links are fine with href */}
            +0 (208) 0599944
          </Link>
        </Box>
      </Flex>
    );
  }
  // --- End Maintenance View ---

  // Main App Layout
  return (
    <div>
      {/* RedirectLink is a component that might handle redirects based on logic */}
      <RedirectLink />

      {/* Conditionally render Navbar, Footer, and Outlet based on the current path */}
      {location.pathname === "/user-info" ? (
        // If on /user-info page, render only Outlet (and potentially other specific layout)
        // You might want to adjust this to include a minimal layout or specific header/footer if needed
        <Box minH={"100vh"}>
          {" "}
          {/* Ensure basic height for the outlet */}
          <SEO /> {/* SEO component might still be needed */}
          <Outlet /> {/* Renders the matched route component */}
        </Box>
      ) : (
        // For all other pages, render the full layout
        <Flex
          minH={"100vh"}
          flexDirection={"column"}
          fontFamily={"Poppins, sans-serif"}
        >
          <TawkToScript /> {/* Tawk.to chat widget */}
          <SEO /> {/* SEO component */}
          <Box>
            <Navbar /> {/* Main navigation bar */}
          </Box>
          <Box flex="1">
            {" "}
            {/* Main content area */}
            <Box minH={"calc(100vh - 100px)"}>
              {" "}
              {/* Adjust height for Navbar/Footer */}
              <main>
                <div className="p-4"></div>
                {/* This div seems empty, consider removing or adding content */}
                <Outlet /> {/* Renders the matched route component */}
              </main>
            </Box>
          </Box>
          {/* Popups are commented out, uncomment if needed */}
          {/* <Popup show={showPopup} onClose={() => setShowPopup(false)} /> */}
          {/* <ContactPopUp show={showContactUs} onClose={() => setshowContactUs(false)} /> */}
          <Box as="footer">
            <Footer /> {/* Footer component */}
          </Box>
        </Flex>
      )}
    </div>
  );
}

export default App;
